import * as THREE from 'three';
import { terrainHeight } from '../engine/terrain';
import type { Box } from '../engine/collision';

// A self-contained shooting test range: target dummies laid out across every cover scenario the
// owner asked for (open ground, behind a cover wall, inside a windowed/doorwayed enclosure, behind
// a tree and a rock, on a raised platform, and a moving patroller), plus the simple cover those
// scenarios need (cover boxes, enclosure walls, a platform, a tree, a rock) with their own
// colliders so bullets and the player collide with them. Targets are tested by the bullet hitTest
// through targets() (a live position + radius per dummy); cover is tested through colliders().
//
// Style mirrors building.ts / nodes.ts: one exported THREE.Group added to the scene, pooled mesh
// instances, colliders as oriented Boxes, and one exported const block for the tunables.

export const RANGE = {
  targetHP: 2, // hits to drop a dummy
  respawnSec: 10, // seconds a dead dummy stays down before it returns at full HP
  targetRadius: 0.5, // hit cylinder radius (also the aim-assist candidate radius)
  moveSpeed: 2.4, // patrol speed of the moving target (world units per second)
  patrolHalf: 5, // half-length of the patrol track (the target swings +/- this from its center)
  // Layout offsets from the range center (x = right of the line, z = downrange). The shooter is
  // expected to stand near the center looking +z; every dummy sits downrange of that.
  laneGap: 4.5, // spacing between adjacent lanes across the line (x)
  nearRow: 10, // downrange distance of the first row of dummies (z)
  farRow: 18, // downrange distance of the back row (z)
  coverWallZ: 7, // a lone cover wall sits this far downrange, its dummy just behind it
  enclosureZ: 14, // the windowed/doorwayed enclosure center, downrange
  enclosureHalf: 2.2, // half-width of the square enclosure
  platformZ: 22, // the raised platform center, downrange (a target up high to test over-cover Z)
  platformH: 2.2, // platform top height above local terrain
  // Dummy body proportions (a readable humanoid).
  bodyH: 1.1, bodyW: 0.5, bodyD: 0.32, // torso box
  headR: 0.22, // head sphere
  legH: 0.7, legW: 0.16, // each leg
  // Cover dimensions.
  coverW: 2.2, coverH: 1.3, coverThick: 0.3, // a chest-high cover box / wall
  wallH: 2.6, wallThick: 0.22, // enclosure walls
  gapW: 1.3, // doorway / window opening width in an enclosure wall
} as const;

let nextId = 0;

interface Target {
  id: number;
  group: THREE.Group; // the whole dummy (parented under the range group)
  pos: THREE.Vector3; // live world position of the dummy center (chest height), read by the bullet hitTest
  base: THREE.Vector3; // feet position the body stands on (for moving + respawn reset)
  hp: number;
  alive: boolean;
  moving: boolean;
  respawn: number; // seconds left until respawn while dead, else 0
  patrolDir: number; // +1 / -1 along the patrol axis, for the moving target
  patrolAxis: THREE.Vector3; // unit direction of the patrol track
  patrolFrom: number; // signed offset from base along the axis at the track ends
}

// The public live view of a target for the bullet hitTest and the aim-assist candidate list. pos is
// the SAME Vector3 the range mutates, so callers always read the current location with no copy.
export interface TargetView {
  id: number;
  pos: THREE.Vector3;
  radius: number;
  alive: boolean;
  moving: boolean;
}

// Shared materials (one each, reused by every pooled instance, like the tier materials elsewhere).
const BODY_MAT = new THREE.MeshLambertMaterial({ color: 0xc24a3a, flatShading: true });
const HEAD_MAT = new THREE.MeshLambertMaterial({ color: 0xd9a066, flatShading: true });
const LIMB_MAT = new THREE.MeshLambertMaterial({ color: 0x6b4a32, flatShading: true });
const DEAD_MAT = new THREE.MeshBasicMaterial({ color: 0x3a3f44 });
const FLASH_MAT = new THREE.MeshBasicMaterial({ color: 0xff5a3c });
const COVER_MAT = new THREE.MeshLambertMaterial({ color: 0x6f7378, flatShading: true });
const PLATFORM_MAT = new THREE.MeshLambertMaterial({ color: 0x7a6b50, flatShading: true });
const ROCK_MAT = new THREE.MeshLambertMaterial({ color: 0x8a8f96, flatShading: true });
const TRUNK_MAT = new THREE.MeshLambertMaterial({ color: 0x5b4329, flatShading: true });
const LEAF_MAT = new THREE.MeshLambertMaterial({ color: 0x3f7a36, flatShading: true });

export class TestRange {
  readonly group = new THREE.Group();
  private readonly tgts: Target[] = [];
  private readonly views: TargetView[] = [];
  private readonly boxes: Box[] = []; // cover colliders (walls, platform, tree, rock)
  private spawned = false;
  private center = new THREE.Vector3();

  // Lay the whole range out around (centerX, centerZ). Calling spawn again first clears the old one
  // so a dev hotkey can re-drop it at the player without stacking ranges.
  spawn(centerX: number, centerZ: number): void {
    if (this.spawned) this.clear();
    this.spawned = true;
    this.center.set(centerX, terrainHeight(centerX, centerZ), centerZ);
    const cx = centerX, cz = centerZ;
    const G = RANGE.laneGap;

    // 1) Open ground: two dummies on the line with no cover at all.
    this.addTarget(cx - G, cz + RANGE.nearRow, false);
    this.addTarget(cx + G, cz + RANGE.nearRow, false);

    // 2) Behind a cover wall: a chest-high box with a dummy crouched-feel just behind it.
    this.addCoverBox(cx + G * 2, cz + RANGE.coverWallZ, 0);
    this.addTarget(cx + G * 2, cz + RANGE.coverWallZ + 1.4, false);

    // 3) Inside a small enclosure: four walls with a doorway on the front and a window on a side,
    // so the dummy inside can only be hit through a gap.
    this.addEnclosure(cx - G * 2, cz + RANGE.enclosureZ);
    this.addTarget(cx - G * 2, cz + RANGE.enclosureZ, false);

    // 4) Behind a tree and behind a rock (simple spawned props with colliders).
    this.addTree(cx + G * 3.2, cz + RANGE.nearRow + 1);
    this.addTarget(cx + G * 3.2, cz + RANGE.nearRow + 2.0, false);
    this.addRock(cx - G * 3.2, cz + RANGE.nearRow + 1);
    this.addTarget(cx - G * 3.2, cz + RANGE.nearRow + 1.9, false);

    // 5) On a raised platform: a target up high to test over-cover Z hits.
    this.addPlatform(cx, cz + RANGE.platformZ);
    this.addTarget(cx, cz + RANGE.platformZ, false, RANGE.platformH);

    // 6) A moving target patrolling left/right across the back of the range.
    const mover = this.addTarget(cx, cz + RANGE.farRow, true);
    mover.patrolAxis.set(1, 0, 0);
    mover.patrolFrom = RANGE.patrolHalf;
  }

  // The live target views for the bullet hitTest + aim-assist candidates. The pos field aliases the
  // target's own vector, so a moving target reports its current spot every frame with no copy.
  targets(): TargetView[] {
    return this.views;
  }

  // Oriented-box colliders for the range's spawned COVER (walls, platform, tree trunk, rock). The
  // dummies are NOT in here; they are tested via targets()/radius so a bullet passes a missed shot
  // through them but stops on real cover. The boxes are stable (built on spawn), so we append the
  // existing objects to `out` — no pool needed.
  collectColliders(out: Box[]): void {
    for (const b of this.boxes) out.push(b);
  }

  // Apply damage to a target. At or below zero HP it dies (drops into a flat dead pose with a brief
  // flash) and queues a respawn. Ignores hits on a dead target. Returns true if the target died on
  // this hit, so the caller can react (score, sound) if it wants.
  damage(id: number, amount: number): boolean {
    const t = this.tgts.find((x) => x.id === id);
    if (!t || !t.alive) return false;
    t.hp -= amount;
    if (t.hp > 0) {
      this.flash(t);
      return false;
    }
    this.kill(t);
    return true;
  }

  // Advance moving targets along their patrol and tick down respawn timers. Cheap when idle.
  update(dt: number): void {
    for (const t of this.tgts) {
      if (t.alive && t.moving) this.patrol(t, dt);
      if (!t.alive) {
        t.respawn -= dt;
        if (t.respawn <= 0) this.revive(t);
      } else {
        // Ease a hit flash back to the body color.
        this.coolFlash(t, dt);
      }
    }
  }

  // Remove every mesh and collider and free the geometry. Materials are shared and kept.
  clear(): void {
    for (const t of this.tgts) {
      this.group.remove(t.group);
      disposeTree(t.group);
    }
    // Dispose the prop meshes that are direct children of the range group (cover, platform, etc).
    for (const o of [...this.group.children]) {
      this.group.remove(o);
      disposeTree(o);
    }
    this.tgts.length = 0;
    this.views.length = 0;
    this.boxes.length = 0;
    this.spawned = false;
  }

  // --- targets ---

  // Build a humanoid dummy at (fx,fz). Its feet sit on the local terrain (plus an optional standH
  // for a platform target); pos is the chest center used by the bullet hitTest. Returns the target
  // so the caller can flag it as moving and set its patrol track.
  private addTarget(fx: number, fz: number, moving: boolean, standH = 0): Target {
    const feetY = terrainHeight(fx, fz) + standH;
    const g = new THREE.Group();
    const legY = RANGE.legH / 2;
    for (const sx of [-1, 1]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(RANGE.legW, RANGE.legH, RANGE.legW), LIMB_MAT);
      leg.position.set(sx * RANGE.bodyW * 0.28, legY, 0);
      g.add(leg);
    }
    const torsoY = RANGE.legH + RANGE.bodyH / 2;
    const torso = new THREE.Mesh(new THREE.BoxGeometry(RANGE.bodyW, RANGE.bodyH, RANGE.bodyD), BODY_MAT);
    torso.position.y = torsoY;
    g.add(torso);
    for (const sx of [-1, 1]) {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(RANGE.legW, RANGE.bodyH * 0.9, RANGE.legW), LIMB_MAT);
      arm.position.set(sx * (RANGE.bodyW / 2 + RANGE.legW / 2), torsoY, 0);
      g.add(arm);
    }
    const headY = RANGE.legH + RANGE.bodyH + RANGE.headR;
    const head = new THREE.Mesh(new THREE.SphereGeometry(RANGE.headR, 12, 10), HEAD_MAT);
    head.position.y = headY;
    g.add(head);
    g.traverse((o) => { o.castShadow = true; o.receiveShadow = true; });
    g.position.set(fx, feetY, fz);
    this.group.add(g);

    const chestY = feetY + torsoY; // chest center in world space
    const t: Target = {
      id: nextId++,
      group: g,
      pos: new THREE.Vector3(fx, chestY, fz),
      base: new THREE.Vector3(fx, feetY, fz),
      hp: RANGE.targetHP,
      alive: true,
      moving,
      respawn: 0,
      patrolDir: 1,
      patrolAxis: new THREE.Vector3(1, 0, 0),
      patrolFrom: RANGE.patrolHalf,
    };
    this.tgts.push(t);
    this.views.push({ id: t.id, pos: t.pos, radius: RANGE.targetRadius, alive: true, moving });
    return t;
  }

  private viewOf(t: Target): TargetView | undefined {
    return this.views.find((v) => v.id === t.id);
  }

  // Death: tip the body flat into a dead pose, recolor it, flash once, and queue the respawn.
  private kill(t: Target): void {
    t.alive = false;
    t.hp = 0;
    t.respawn = RANGE.respawnSec;
    t.group.rotation.x = -Math.PI / 2; // fall onto its back
    t.group.position.y = t.base.y + 0.1; // settle near the ground
    recolor(t.group, DEAD_MAT);
    const v = this.viewOf(t);
    if (v) v.alive = false;
  }

  // Respawn: stand back up at full HP at the base position.
  private revive(t: Target): void {
    t.alive = true;
    t.hp = RANGE.targetHP;
    t.respawn = 0;
    t.group.rotation.x = 0;
    t.group.position.copy(t.base);
    t.pos.set(t.base.x, t.base.y + RANGE.legH + RANGE.bodyH / 2, t.base.z);
    recolorReset(t.group);
    const v = this.viewOf(t);
    if (v) v.alive = true;
  }

  // A brief hit flash: swap the torso to the flash color; coolFlash eases it back over a frame or two.
  private flash(t: Target): void {
    const torso = t.group.children.find((o) => (o as THREE.Mesh).material === BODY_MAT) as THREE.Mesh | undefined;
    if (torso) torso.material = FLASH_MAT;
    t.respawn = 0.12; // reuse the timer field as the flash hold while alive
  }

  private coolFlash(t: Target, dt: number): void {
    if (t.respawn <= 0) return;
    t.respawn -= dt;
    if (t.respawn <= 0) {
      const torso = t.group.children.find((o) => (o as THREE.Mesh).material === FLASH_MAT) as THREE.Mesh | undefined;
      if (torso) torso.material = BODY_MAT;
      t.respawn = 0;
    }
  }

  // Slide a moving target back and forth along its patrol axis, keeping its body and hit pos in sync.
  private patrol(t: Target, dt: number): void {
    const step = RANGE.moveSpeed * dt * t.patrolDir;
    // Project the current offset from base along the axis, advance it, and clamp to the track ends.
    let off = (t.pos.x - t.base.x) * t.patrolAxis.x + (t.pos.z - t.base.z) * t.patrolAxis.z;
    off += step;
    if (off > t.patrolFrom) { off = t.patrolFrom; t.patrolDir = -1; }
    else if (off < -t.patrolFrom) { off = -t.patrolFrom; t.patrolDir = 1; }
    const wx = t.base.x + t.patrolAxis.x * off;
    const wz = t.base.z + t.patrolAxis.z * off;
    // Track terrain under the moving feet so a patroller on a slope stays grounded.
    const feetY = terrainHeight(wx, wz);
    t.group.position.set(wx, feetY, wz);
    t.pos.set(wx, feetY + RANGE.legH + RANGE.bodyH / 2, wz);
  }

  // --- cover ---

  // A chest-high cover box with its collider. yaw rotates it about Y.
  private addCoverBox(x: number, z: number, yaw: number): void {
    const y = terrainHeight(x, z) + RANGE.coverH / 2;
    const m = new THREE.Mesh(new THREE.BoxGeometry(RANGE.coverW, RANGE.coverH, RANGE.coverThick), COVER_MAT);
    m.position.set(x, y, z);
    m.rotation.y = yaw;
    m.castShadow = true; m.receiveShadow = true;
    this.group.add(m);
    this.boxes.push({ cx: x, cy: y, cz: z, hx: RANGE.coverW / 2, hy: RANGE.coverH / 2, hz: RANGE.coverThick / 2, yaw });
  }

  // A small square enclosure: a front wall with a doorway gap, a back wall, one solid side wall, and
  // one side wall with a window gap. The dummy inside can only be hit through the doorway or window.
  private addEnclosure(cx: number, cz: number): void {
    const h = RANGE.enclosureHalf;
    // Front wall (faces the shooter, downrange-near side): doorway gap at ground level.
    this.addGapWall(cx, cz - h, h * 2, 0, 0, RANGE.wallH); // doorway: gap from the floor up
    // Back wall: solid.
    this.addSolidWall(cx, cz + h, h * 2, 0);
    // Left side: solid.
    this.addSolidWall(cx - h, cz, h * 2, Math.PI / 2);
    // Right side: a window gap (raised off the floor).
    this.addGapWall(cx + h, cz, h * 2, Math.PI / 2, 0.9, 0.9 + 1.0); // window: gap 0.9 .. 1.9 up
  }

  // A solid wall segment (full height) with its collider.
  private addSolidWall(x: number, z: number, len: number, yaw: number): void {
    const y = terrainHeight(x, z) + RANGE.wallH / 2;
    const m = new THREE.Mesh(new THREE.BoxGeometry(len, RANGE.wallH, RANGE.wallThick), COVER_MAT);
    m.position.set(x, y, z);
    m.rotation.y = yaw;
    m.castShadow = true; m.receiveShadow = true;
    this.group.add(m);
    this.boxes.push({ cx: x, cy: y, cz: z, hx: len / 2, hy: RANGE.wallH / 2, hz: RANGE.wallThick / 2, yaw });
  }

  // A wall with a centered passable gap from gapBottom to gapTop (measured up from the floor). Built
  // as two side posts plus a sill below the gap and a lintel above it, each with its own collider, so
  // the gap is truly see-through and shoot-through while the rest of the wall is solid.
  private addGapWall(x: number, z: number, len: number, yaw: number, gapBottom: number, gapTop: number): void {
    const baseY = terrainHeight(x, z);
    const t = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw)); // wall's length axis
    const postLen = (len - RANGE.gapW) / 2;
    const off = (len - postLen) / 2;
    for (const sgn of [-1, 1]) {
      const px = x + t.x * off * sgn, pz = z + t.z * off * sgn;
      const y = baseY + RANGE.wallH / 2;
      const m = new THREE.Mesh(new THREE.BoxGeometry(postLen, RANGE.wallH, RANGE.wallThick), COVER_MAT);
      m.position.set(px, y, pz);
      m.rotation.y = yaw;
      m.castShadow = true; m.receiveShadow = true;
      this.group.add(m);
      this.boxes.push({ cx: px, cy: y, cz: pz, hx: postLen / 2, hy: RANGE.wallH / 2, hz: RANGE.wallThick / 2, yaw });
    }
    // Sill below the gap (when the gap starts above the floor, like a window).
    if (gapBottom > 1e-3) {
      const y = baseY + gapBottom / 2;
      const m = new THREE.Mesh(new THREE.BoxGeometry(RANGE.gapW, gapBottom, RANGE.wallThick), COVER_MAT);
      m.position.set(x, y, z);
      m.rotation.y = yaw;
      m.castShadow = true; m.receiveShadow = true;
      this.group.add(m);
      this.boxes.push({ cx: x, cy: y, cz: z, hx: RANGE.gapW / 2, hy: gapBottom / 2, hz: RANGE.wallThick / 2, yaw });
    }
    // Lintel above the gap, up to the wall top.
    const lintelH = RANGE.wallH - gapTop;
    if (lintelH > 1e-3) {
      const y = baseY + gapTop + lintelH / 2;
      const m = new THREE.Mesh(new THREE.BoxGeometry(RANGE.gapW, lintelH, RANGE.wallThick), COVER_MAT);
      m.position.set(x, y, z);
      m.rotation.y = yaw;
      m.castShadow = true; m.receiveShadow = true;
      this.group.add(m);
      this.boxes.push({ cx: x, cy: y, cz: z, hx: RANGE.gapW / 2, hy: lintelH / 2, hz: RANGE.wallThick / 2, yaw });
    }
  }

  // A raised platform a target stands on (top at platformH above terrain), with a box collider so the
  // player can climb on and bullets stop on its body.
  private addPlatform(x: number, z: number): void {
    const baseY = terrainHeight(x, z);
    const w = 2.6, thick = RANGE.platformH;
    const y = baseY + thick / 2;
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, thick, w), PLATFORM_MAT);
    m.position.set(x, y, z);
    m.castShadow = true; m.receiveShadow = true;
    this.group.add(m);
    this.boxes.push({ cx: x, cy: y, cz: z, hx: w / 2, hy: thick / 2, hz: w / 2, yaw: 0 });
  }

  // A simple tree (trunk cylinder + cone canopy). Only the trunk gets a collider, matching how the
  // node trees collide, so a bullet stops on the trunk but passes through the leaves.
  private addTree(x: number, z: number): void {
    const baseY = terrainHeight(x, z);
    const trunkH = 2.2, trunkR = 0.22;
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(trunkR * 0.8, trunkR, trunkH, 7), TRUNK_MAT);
    trunk.position.set(x, baseY + trunkH / 2, z);
    trunk.castShadow = true; trunk.receiveShadow = true;
    this.group.add(trunk);
    const leaves = new THREE.Mesh(new THREE.ConeGeometry(1.2, 2.4, 8), LEAF_MAT);
    leaves.position.set(x, baseY + trunkH + 0.7, z);
    leaves.castShadow = true; leaves.receiveShadow = true;
    this.group.add(leaves);
    this.boxes.push({ cx: x, cy: baseY + trunkH / 2, cz: z, hx: trunkR, hy: trunkH / 2, hz: trunkR, yaw: 0 });
  }

  // A simple rock (low icosahedron) with a box collider sized to its body.
  private addRock(x: number, z: number): void {
    const baseY = terrainHeight(x, z);
    const r = 0.9;
    const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), ROCK_MAT);
    rock.scale.y = 0.7;
    rock.position.set(x, baseY + r * 0.45, z);
    rock.castShadow = true; rock.receiveShadow = true;
    this.group.add(rock);
    const he = r * 0.8;
    this.boxes.push({ cx: x, cy: baseY + r * 0.45, cz: z, hx: he, hy: r * 0.55, hz: he, yaw: 0 });
  }
}

// Swap every mesh in a group to one material (death recolor). The original materials are shared and
// restored by recolorReset, so nothing is disposed here.
function recolor(g: THREE.Object3D, mat: THREE.Material): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) m.material = mat;
  });
}

// Restore the dummy's body/head/limb materials after a respawn (by mesh shape: sphere = head, the
// tall torso box = body, the rest = limbs).
function recolorReset(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const geo = m.geometry;
    if (geo instanceof THREE.SphereGeometry) m.material = HEAD_MAT;
    else if (geo instanceof THREE.BoxGeometry && geo.parameters.height === RANGE.bodyH) m.material = BODY_MAT;
    else m.material = LIMB_MAT;
  });
}

// Free every geometry under an object (materials are shared and kept), mirroring disposeSolid/grass.
function disposeTree(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
  });
}
